import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiUser, 
  FiClock,
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiAlertTriangle,
  FiTrendingUp,
  FiX,
  FiBarChart2,
  FiUsers,
  FiShield,
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import logo from '../../../assets/T.png';

const BlogHiddenCostSpreadsheets = () => {
  const navigate = useNavigate();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  // SEO structured data
  useEffect(() => {
    document.title = "The Hidden Cost of Using Spreadsheets for Administration in Indian Education | Truyon";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Discover the hidden costs of using spreadsheets for school administration in India. Learn why Indian institutions are switching to ERP systems for better efficiency.";
    }

    // Schema markup
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "The Hidden Cost of Using Spreadsheets for Administration in Indian Education",
      "description": "Discover the hidden costs of using spreadsheets for school administration in India. Learn why Indian institutions are switching to ERP systems for better efficiency.",
      "author": {
        "@type": "Organization",
        "name": "Truyon"
      },
      "datePublished": "2026-08-13",
      "dateModified": "2026-08-13"
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const hiddenCosts = [
    {
      id: 'time-waste',
      icon: <FiClock className="w-6 h-6 text-indigo-600" />,
      title: 'The Time Drain Crisis',
      description: 'Administrators in Indian schools spend an average of 15-20 hours per week managing spreadsheets—data entry, verification, formatting, and fixing broken formulas. That\'s nearly 1000 hours per year lost to manual work.',
      stat: 'Schools waste 1000+ hours annually on spreadsheet management',
      solution: 'Truyon automates data entry and calculations, saving 80% of admin time.',
      color: 'bg-indigo-50'
    },
    {
      id: 'data-errors',
      icon: <FiAlertTriangle className="w-6 h-6 text-rose-600" />,
      title: 'The Error Epidemic',
      description: 'Spreadsheets are notoriously error-prone. A single mis-entered cell or broken formula can lead to incorrect fee calculations, attendance records, or grade sheets—affecting thousands of students and parents.',
      stat: '88% of spreadsheets contain critical errors',
      solution: 'Truyon ensures data integrity with validation rules and automated calculations.',
      color: 'bg-rose-50'
    },
    {
      id: 'security',
      icon: <FiShield className="w-6 h-6 text-amber-600" />,
      title: 'The Security Nightmare',
      description: 'Spreadsheets shared via email or USB drives create massive security risks. Student data, financial records, and personal information of staff are vulnerable to unauthorized access, loss, or theft.',
      stat: '70% of schools have no data backup or security protocol',
      solution: 'Truyon provides enterprise-grade security with encrypted cloud storage and access controls.',
      color: 'bg-amber-50'
    },
    {
      id: 'collaboration',
      icon: <FiUsers className="w-6 h-6 text-emerald-600" />,
      title: 'The Collaboration Breakdown',
      description: 'Managing multiple spreadsheet versions creates chaos. One person updates attendance, another updates grades, and suddenly there are 15 different files with conflicting data, making team coordination impossible.',
      stat: 'Administrators waste 3+ hours weekly resolving data conflicts',
      solution: 'Truyon enables real-time collaboration with a single source of truth.',
      color: 'bg-emerald-50'
    },
    {
      id: 'reporting',
      icon: <FiBarChart2 className="w-6 h-6 text-purple-600" />,
      title: 'The Reporting Nightmare',
      description: 'Generating reports from spreadsheets is a manual nightmare. Each report takes hours to compile, format, and verify. By the time the report is ready, the data is already outdated for decision-making.',
      stat: 'Schools spend 50% of reporting time on manual spreadsheet tasks',
      solution: 'Truyon generates real-time reports with one click, with accurate, up-to-date data.',
      color: 'bg-purple-50'
    },
    {
      id: 'scalability',
      icon: <FiTrendingUp className="w-6 h-6 text-blue-600" />,
      title: 'The Scalability Ceiling',
      description: 'Spreadsheets work fine for 50 students, but break down at 500 or 5000. As schools grow, spreadsheets become slower, harder to manage, and impossible to scale—creating a bottleneck to institutional growth.',
      stat: 'Spreadsheets fail for 80% of schools with over 500 students',
      solution: 'Truyon scales seamlessly from 50 to 50,000 students with zero performance loss.',
      color: 'bg-blue-50'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={logo} alt="Truyon Logo" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold tracking-tight text-slate-900">truyon</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsDemoModalOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Request Demo
            </button>
            <Link to="/" className="text-slate-600 hover:text-slate-900 p-2 rounded-lg hover:bg-slate-100 transition-colors">
              <FiArrowLeft className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Blog Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-indigo-600 font-semibold mb-3">
            <span className="bg-indigo-50 px-3 py-1 rounded-full">Blog</span>
            <span className="text-slate-400">•</span>
            <span className="text-slate-500">August 13, 2026</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
            The Hidden Cost of Using Spreadsheets for Administration in Indian Education
          </h1>
          
          <p className="mt-4 text-lg sm:text-xl text-slate-600">
            Why the 'free' tool is actually costing your institution millions in lost time and productivity.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <FiUser className="w-4 h-4" />
              <span>By Truyon Team</span>
            </div>
            <div className="flex items-center gap-2">
              <FiClock className="w-4 h-4" />
              <span>10 min read</span>
            </div>
          </div>
        </div>

        {/* Stats Highlight */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-3xl font-extrabold text-indigo-600">1000+</div>
            <div className="text-sm text-slate-500">Hours wasted annually</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-3xl font-extrabold text-rose-600">88%</div>
            <div className="text-sm text-slate-500">Spreadsheets contain errors</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-3xl font-extrabold text-amber-600">₹2.5L+</div>
            <div className="text-sm text-slate-500">Hidden cost per year per school</div>
          </div>
        </div>

        {/* Introduction */}
        <div className="prose prose-slate max-w-none mb-8">
          <p className="text-lg text-slate-600 leading-relaxed">
            For decades, Indian schools, colleges, and universities have relied on spreadsheets—Microsoft Excel, Google Sheets, or LibreOffice Calc—to manage their administration. They're familiar, accessible, and seemingly "free." 
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            But here's the uncomfortable truth: <strong className="text-slate-900">spreadsheets are costing your institution far more than you realize.</strong> The hidden costs—in time, errors, security, and missed opportunities—add up to millions of rupees wasted every year.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            Let's break down the real cost of using spreadsheets in Indian educational administration.
          </p>
        </div>

        {/* Hidden Costs */}
        <div className="space-y-8">
          {hiddenCosts.map((cost, index) => (
            <motion.div
              key={index}
              id={cost.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`${cost.color} rounded-2xl p-6 sm:p-8 border border-slate-200/60`}
            >
              <div className="flex items-start gap-4">
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  {cost.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                    <span className="bg-white px-2.5 py-0.5 rounded-full text-xs font-bold">Hidden Cost {index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{cost.title}</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">{cost.description}</p>
                  
                  <div className="bg-white/60 rounded-xl p-4 mb-4 border border-slate-200/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 mb-1">
                      <FiAlertTriangle className="w-4 h-4" />
                      <span>The Hidden Cost</span>
                    </div>
                    <p className="text-sm text-slate-700">{cost.stat}</p>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-emerald-200/60">
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                      <FiCheckCircle className="w-4 h-4" />
                      <span>Truyon's Solution</span>
                    </div>
                    <p className="text-sm text-slate-700">{cost.solution}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Financial Impact Section */}
        <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">The Real Financial Impact</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="text-sm font-semibold text-red-700 mb-1">Cost of Spreadsheets</div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• ₹2,50,000+ in staff time annually</li>
                <li>• ₹50,000+ in data error corrections</li>
                <li>• ₹1,00,000+ in lost productivity</li>
                <li>• Unlimited opportunity cost</li>
              </ul>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <div className="text-sm font-semibold text-emerald-700 mb-1">With Truyon ERP</div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• 80% reduction in admin time</li>
                <li>• 95% fewer data errors</li>
                <li>• Real-time collaboration</li>
                <li>• Zero data security incidents</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Conclusion */}
        <div className="mt-12 bg-indigo-600 rounded-2xl p-8 text-white">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold mb-3">Stop Wasting Time and Money</h2>
            <p className="text-indigo-100 leading-relaxed">
              Spreadsheets were never designed for running an educational institution. Every hour spent wrestling with spreadsheets is an hour taken away from your students, teachers, and mission.
            </p>
            <p className="text-indigo-100 leading-relaxed mt-3">
              Truyon automates the tedious, eliminates errors, and gives you back the time to focus on what matters—providing quality education.
            </p>
            <button
              onClick={() => setIsDemoModalOpen(true)}
              className="mt-6 px-8 py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-indigo-50 transition-colors inline-flex items-center gap-2"
            >
              Sign Up Free <FiArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Author Bio */}
        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 flex items-start gap-4">
          <div className="w-12 h-12 bg-indigo-100 text-indigo-700 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0">
            T
          </div>
          <div>
            <h4 className="font-bold text-slate-900">Truyon Education Team</h4>
            <p className="text-sm text-slate-500">Helping Indian educational institutions modernize their administration with smart, affordable ERP solutions.</p>
          </div>
        </div>
      </main>

      {/* Demo Request Modal */}
      {isDemoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-900">Sign Up Free</h3>
              <button
                onClick={() => setIsDemoModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-600 text-sm mb-6">
              Stop losing time and money to spreadsheets. Start your free Truyon journey today.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setIsDemoModalOpen(false);
                  navigate('/signup');
                }}
                className="w-full py-3 px-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold"
              >
                Sign Up Free
              </button>
              <button
                onClick={() => {
                  setIsDemoModalOpen(false);
                  navigate('/request-demo');
                }}
                className="w-full py-3 px-6 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogHiddenCostSpreadsheets;